import React, { useEffect } from "react";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { connect } from "trim-redux";
import { readAdminForegroundOrderNotificationsEnabled } from "../../../config/adminOrderNotificationsPref";
import {
  NEW_MESSAGE_SOUND,
  NEW_ORDER_SOUND,
} from "../../../assets/sounds/notificationSound";

const MIN_MS_BETWEEN_MESSAGE_SOUND_STARTS = 1 * 10 * 1000;
const DEDUP_WINDOW_MS = 10 * 1000;
const AUDIO_UNLOCK_KEY = "kd_notif_audio_unlocked";

const SILENT_WAV =
  "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAE=";

let messageAudioUnlocked = false;
let lastMessageSoundStartTime = 0;
const recentForegroundNotificationKeys = new Map();

function resolvePayloadTitle(payload) {
  const n = payload?.notification || {};
  const data = payload?.data || {};
  return String(n.title || data.title || "").trim();
}

function resolvePayloadBody(payload) {
  const n = payload?.notification || {};
  const data = payload?.data || {};
  return String(n.body || data.body || "").trim();
}

function resolvePayloadLink(payload) {
  const data = payload?.data || {};
  return (
    payload?.fcmOptions?.link ||
    data.link ||
    data.url ||
    payload?.webpush?.fcm_options?.link ||
    ""
  );
}

function resolveInAppPath(rawLink) {
  const s = String(rawLink || "").trim();
  if (!s) {
    return null;
  }
  if (s.startsWith("http://") || s.startsWith("https://")) {
    try {
      const url = new URL(s);
      if (typeof window !== "undefined" && url.origin === window.location.origin) {
        return `${url.pathname}${url.search}${url.hash}`;
      }
      if (typeof window !== "undefined") {
        window.location.href = s;
      }
      return null;
    } catch {
      return null;
    }
  }
  return s.startsWith("/") ? s : `/${s}`;
}

function resolveNotificationSoundSrc(payload) {
  const raw = resolvePayloadTitle(payload);
  const lower =
    typeof raw.toLocaleLowerCase === "function" ?
      raw.toLocaleLowerCase("tr-TR")
    : raw.toLowerCase();
  const asciiFold = lower
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i")
    .replace(/ş/g, "s")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c");
  if (
    lower.includes("sipariş") ||
    lower.includes("siparis") ||
    asciiFold.includes("siparis") ||
    asciiFold.includes("yeni siparis")
  ) {
    return NEW_ORDER_SOUND;
  }
  return NEW_MESSAGE_SOUND;
}

function isOrderLikeForegroundFcmPayload(payload) {
  return resolveNotificationSoundSrc(payload) === NEW_ORDER_SOUND;
}

function buildForegroundNotificationDedupKey(payload) {
  const messageId = String(payload?.messageId || "").trim();
  if (messageId) {
    return `mid:${messageId}`;
  }
  return `t:${resolvePayloadTitle(payload)}|b:${resolvePayloadBody(payload)}`;
}

function shouldSkipDuplicateForegroundNotification(payload) {
  const key = buildForegroundNotificationDedupKey(payload);
  const now = Date.now();
  const lastSeen = recentForegroundNotificationKeys.get(key);
  if (lastSeen != null && now - lastSeen < DEDUP_WINDOW_MS) {
    return true;
  }
  recentForegroundNotificationKeys.set(key, now);
  for (const [storedKey, seenAt] of recentForegroundNotificationKeys) {
    if (now - seenAt > DEDUP_WINDOW_MS) {
      recentForegroundNotificationKeys.delete(storedKey);
    }
  }
  return false;
}

function markAudioUnlocked() {
  messageAudioUnlocked = true;
  try {
    sessionStorage.setItem(AUDIO_UNLOCK_KEY, "1");
  } catch {
    /* ignore */
  }
}

function tryUnlockMessageAudio() {
  if (messageAudioUnlocked || typeof Audio === "undefined") {
    return;
  }
  try {
    const audio = new Audio(SILENT_WAV);
    audio.volume = 0.0001;
    void audio.play().then(() => {
      markAudioUnlocked();
    }).catch(() => {});
  } catch {
    messageAudioUnlocked = false;
  }
}

function isPageActiveForInAppNotifier() {
  if (typeof document === "undefined") {
    return false;
  }
  return document.visibilityState === "visible" && document.hasFocus();
}

function requestBackgroundNotificationWithSound(payload) {
  if (
    typeof navigator === "undefined" ||
    !navigator.serviceWorker ||
    !payload
  ) {
    return;
  }
  void navigator.serviceWorker.ready
    .then((registration) => {
      registration.active?.postMessage({
        type: "show-linked-notification",
        payload,
      });
    })
    .catch(() => {});
}

function playNotificationSound(src) {
  if (typeof Audio === "undefined" || !src) {
    return;
  }

  const isOrderSound = src === NEW_ORDER_SOUND;
  if (!isOrderSound) {
    if (!messageAudioUnlocked) {
      return;
    }
    const now = Date.now();
    if (
      lastMessageSoundStartTime > 0 &&
      now - lastMessageSoundStartTime < MIN_MS_BETWEEN_MESSAGE_SOUND_STARTS
    ) {
      return;
    }
    lastMessageSoundStartTime = now;
  }

  try {
    const audio = new Audio(src);
    audio.volume = 0.85;
    void audio.play().catch((err) => {
      if (process.env.NODE_ENV === "development") {
        console.warn("FCM notification sound play rejected:", err);
      }
    });
  } catch (e) {
    if (process.env.NODE_ENV === "development") {
      console.warn("notification sound error", e);
    }
  }
}

function FcmForegroundNotifier({ user }) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      if (sessionStorage.getItem(AUDIO_UNLOCK_KEY) === "1") {
        messageAudioUnlocked = true;
      }
    } catch {
      /* ignore */
    }

    const onUserGesture = () => {
      tryUnlockMessageAudio();
    };
    document.addEventListener("pointerdown", onUserGesture, true);
    document.addEventListener("click", onUserGesture, true);
    document.addEventListener("keydown", onUserGesture, true);
    document.addEventListener("touchstart", onUserGesture, true);
    return () => {
      document.removeEventListener("pointerdown", onUserGesture, true);
      document.removeEventListener("click", onUserGesture, true);
      document.removeEventListener("keydown", onUserGesture, true);
      document.removeEventListener("touchstart", onUserGesture, true);
    };
  }, []);

  useEffect(() => {
    const handler = (event) => {
      try {
        const payload = event.detail;
        if (!payload) {
          return;
        }
        if (shouldSkipDuplicateForegroundNotification(payload)) {
          return;
        }

        if (!isPageActiveForInAppNotifier()) {
          requestBackgroundNotificationWithSound(payload);
          return;
        }

        const rawLink = resolvePayloadLink(payload);
        const inAppPath = resolveInAppPath(rawLink);
        if (!inAppPath) {
          return;
        }

        const isAdmin = user?.role?.name === "admin";
        if (
          isAdmin &&
          isOrderLikeForegroundFcmPayload(payload) &&
          !readAdminForegroundOrderNotificationsEnabled()
        ) {
          return;
        }

        const title =
          resolvePayloadTitle(payload) || "Kolay Doy";
        const body = resolvePayloadBody(payload);
        const key = `fcm-${payload.messageId || `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`}`;

        const onClick = () => {
          tryUnlockMessageAudio();
          navigate(inAppPath);
          closeSnackbar(key);
        };

        enqueueSnackbar(
          <Box
            role="button"
            onClick={onClick}
            sx={{ cursor: "pointer", maxWidth: 360 }}
          >
            <Typography component="div" variant="subtitle2" fontWeight={700}>
              {title}
            </Typography>
            {body ?
              <Typography
                component="div"
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {body}
              </Typography>
            : null}
          </Box>,
          {
            key,
            variant: "info",
            anchorOrigin: { vertical: "bottom", horizontal: "right" },
            autoHideDuration: 9000,
          },
        );

        playNotificationSound(resolveNotificationSoundSrc(payload));
      } catch (e) {
        console.error("FcmForegroundNotifier handler failed", e);
      }
    };

    window.addEventListener("fcm-foreground-message", handler);
    return () => window.removeEventListener("fcm-foreground-message", handler);
  }, [enqueueSnackbar, closeSnackbar, navigate, user]);

  return null;
}

const mstp = (state) => ({ user: state.user });

export default connect(mstp)(FcmForegroundNotifier);
